import type { CSSProperties } from 'react';
import type { ParagraphStyle, TextContentData, TextElement, TypographyStyle } from '@/components/canvas/document/publicTypes';
import { DEFAULT_DESIGNER_FONT } from '@/components/canvas/shared/constants/fonts';
import { TEXT_ELEMENT_DEFAULT_LINE_HEIGHT } from '../constants';
import { detectBlockKind } from '../utils/textContent';
import type { InlineTextStyle, ListStyle, TextBlockType } from './textDocument';

export const TEXT_BLOCK_PRESETS: Record<
  TextBlockType,
  { fontSizeScale: number; fontWeight: number; placeholder: string }
> = {
  h1: { fontSizeScale: 1, fontWeight: 700, placeholder: '输入一级标题' },
  h2: { fontSizeScale: 1, fontWeight: 700, placeholder: '输入二级标题' },
  h3: { fontSizeScale: 1, fontWeight: 600, placeholder: '输入三级标题' },
  paragraph: { fontSizeScale: 1, fontWeight: 400, placeholder: '输入内容' },
  'ordered-list-item': { fontSizeScale: 1, fontWeight: 400, placeholder: '输入列表项' },
  'unordered-list-item': { fontSizeScale: 1, fontWeight: 400, placeholder: '输入列表项' },
};

export const DEFAULT_TEXT_TYPOGRAPHY: TypographyStyle = {
  fontFamily: DEFAULT_DESIGNER_FONT,
  fontSize: 16,
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#000000',
  lineHeight: TEXT_ELEMENT_DEFAULT_LINE_HEIGHT,
};

export const DEFAULT_PARAGRAPH_STYLE: ParagraphStyle = {
  textAlign: 'left',
};

export const DEFAULT_LIST_STYLE: ListStyle = {
  markerGap: 2,
  indent: 16,
  unorderedMarker: 'disc',
};

export function mergeTypographyStyle(base: TypographyStyle, patch?: Partial<TypographyStyle>): TypographyStyle {
  return {
    ...base,
    ...patch,
    fontFamily: patch?.fontFamily ?? base.fontFamily,
    fontSize: patch?.fontSize ?? base.fontSize,
    fontWeight: patch?.fontWeight ?? base.fontWeight,
    color: patch?.color ?? base.color,
  };
}

export function resolveElementTextTypography(element: Pick<TextElement, 'typography'>): TypographyStyle {
  return mergeTypographyStyle(DEFAULT_TEXT_TYPOGRAPHY, element.typography);
}

export function resolveElementTextParagraph(element: Pick<TextElement, 'paragraph'>): ParagraphStyle {
  return { ...DEFAULT_PARAGRAPH_STYLE, ...element.paragraph };
}

export function resolveElementTextList(paragraph: ParagraphStyle): ListStyle {
  return { ...DEFAULT_LIST_STYLE, ...paragraph.list };
}

export function resolveBlockTextStyle(
  element: Pick<TextElement, 'typography'>,
  blockType: TextBlockType,
  runStyle?: InlineTextStyle,
): CSSProperties {
  const shell = resolveElementTextTypography(element);
  const preset = TEXT_BLOCK_PRESETS[blockType];
  const fontSize = runStyle?.fontSize ?? Math.round(shell.fontSize * preset.fontSizeScale);
  const fontWeight = runStyle?.fontWeight ?? (runStyle?.bold ? 700 : shell.fontWeight);
  const fontStyle = runStyle?.fontStyle ?? (runStyle?.italic ? 'italic' : shell.fontStyle ?? 'normal');
  const decorations = [
    runStyle?.underline ? 'underline' : '',
    runStyle?.strike ? 'line-through' : '',
  ].filter(Boolean);

  return {
    fontFamily: runStyle?.fontFamily ?? shell.fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    color: runStyle?.color ?? shell.color,
    lineHeight: shell.lineHeight,
    letterSpacing: runStyle?.letterSpacing ?? shell.letterSpacing,
    textDecorationLine: decorations.length ? decorations.join(' ') : undefined,
    backgroundColor: runStyle?.backgroundColor,
    verticalAlign: runStyle?.superscript ? 'super' : runStyle?.subscript ? 'sub' : undefined,
  };
}

export function getTextLayoutFromElement(element: Pick<TextElement, 'typography' | 'paragraph' | 'data'>): {
  typography: TypographyStyle;
  paragraph: ParagraphStyle;
  list: ListStyle;
  writingMode: 'horizontal-tb' | 'vertical-rl';
} {
  return {
    typography: resolveElementTextTypography(element),
    paragraph: resolveElementTextParagraph(element),
    list: resolveElementTextList(element.paragraph),
    writingMode: element.paragraph.writingMode ?? 'horizontal-tb',
  };
}

export function isVerticalTextWritingMode(writingMode: CSSProperties['writingMode']): boolean {
  return writingMode === 'vertical-rl';
}

export function getTextPlaceholderForBlockType(blockType: TextBlockType): string {
  return TEXT_BLOCK_PRESETS[blockType].placeholder;
}

/** 元素级 placeholder：存储字段 → 块类型内置预设 */
export function resolveElementPlaceholder(text: TextContentData, paragraph?: ParagraphStyle): string {
  if (paragraph?.placeholder?.trim()) return paragraph.placeholder.trim();
  return getTextPlaceholderForBlockType(detectBlockKind(text.document));
}

/** 展示态行内容：行首/行尾禁则（标点避头尾），与导出 canvas 的 strict 规则一致。 */
export function resolveTextLineContentDisplayStyle(
  singleLine: boolean,
  writingMode: CSSProperties['writingMode'] = 'horizontal-tb',
): CSSProperties {
  const isVertical = isVerticalTextWritingMode(writingMode);
  return {
    display: isVertical ? 'inline-block' : undefined,
    flex: '1 1 auto',
    minWidth: isVertical ? '1em' : 0,
    minHeight: isVertical ? 0 : undefined,
    height: isVertical ? '100%' : undefined,
    whiteSpace: singleLine ? 'pre' : 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    lineBreak: 'strict',
  };
}

/** 编辑态行内容：在 flex 块内可收缩，并允许 CJK/长串按宽度断字换行。 */
export function resolveTextLineContentEditStyle(
  singleLine: boolean,
  writingMode: CSSProperties['writingMode'] = 'horizontal-tb',
): CSSProperties {
  const isVertical = isVerticalTextWritingMode(writingMode);
  return {
    display: isVertical ? 'inline-block' : undefined,
    flex: '1 1 auto',
    minWidth: isVertical ? '1em' : 0,
    minHeight: isVertical ? 0 : undefined,
    height: isVertical ? '100%' : undefined,
    whiteSpace: singleLine ? 'pre' : 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineBreak: 'anywhere',
  };
}

/**
 * Convert a pixel-based list dimension (designed at 16px base font) to an em string.
 * The divisor MUST be the fixed design baseline (16), NOT the current font size —
 * because `em` in CSS is already relative to the element's own font-size.
 * Using the current font size would cancel out, leaving the original px value unchanged.
 *
 * Example: indent=24
 *   → 24/16 = 1.5em
 *   → At 16px font: 1.5em = 24px (same as design)
 *   → At 32px font: 1.5em = 48px (scales proportionally) ✓
 *   → At 48px font: 1.5em = 72px (accommodates wide markers like "12.") ✓
 */
const LIST_DESIGN_BASELINE = 16;
function listPxToEm(px: number): string {
  return `${(px / LIST_DESIGN_BASELINE).toFixed(4)}em`;
}

/** 列表行容器：编辑态与展示态统一用 columnGap 控制符号与正文间距。 */
export function resolveTextListBlockRowStyle(
  blockStyle: CSSProperties,
  list: ListStyle,
  isList: boolean,
  writingMode: CSSProperties['writingMode'] = 'horizontal-tb',
): CSSProperties {
  const isVertical = isVerticalTextWritingMode(writingMode);
  return {
    ...blockStyle,
    display: isVertical && !isList ? 'block' : 'flex',
    alignItems: isVertical && !isList ? undefined : 'baseline',
    width: isVertical ? 'auto' : '100%',
    height: isVertical ? '100%' : undefined,
    minWidth: isVertical ? '1em' : undefined,
    // em-based gap scales proportionally with font size.
    ...(isList ? { columnGap: listPxToEm(list.markerGap) } : {}),
  };
}

/** 列表序号/符号：仅保留字号等排版，禁止断行；间距由行容器 columnGap 控制。 */
export function resolveTextListMarkerStyle(
  list: ListStyle,
  blockStyle: CSSProperties,
): CSSProperties {
  // minWidth reserves space for single-digit markers (e.g. "1."); for two-digit or wider
  // markers (e.g. "10.") the container auto-expands to fit content — no clipping.
  const indentEm = listPxToEm(list.indent);
  return {
    fontFamily: blockStyle.fontFamily,
    fontSize: blockStyle.fontSize,
    fontWeight: blockStyle.fontWeight,
    fontStyle: blockStyle.fontStyle,
    color: blockStyle.color,
    flex: '0 0 auto',
    minWidth: indentEm,
    boxSizing: 'border-box',
    // Left-align so the marker character starts at the container's left edge, eliminating
    // the visual impression of extra left margin caused by right-aligned empty space.
    textAlign: 'left',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    lineBreak: 'auto',
    flexShrink: 0,
    margin: 0,
    padding: 0,
  };
}
